// Financials — Patrick's runoff report as a live matrix, plus GC Burn-Down.
//
// Mirrors the Excel exactly:
//  · Jobs Under Contract (WFP Status=Active): key a % of contract per month.
//    Month $ = Backlog × (month% ÷ total keyed %), Backlog = Contract × (1 − %complete).
//    The scaling makes the months sum exactly to the backlog, like the sheet.
//  · Probable Future Jobs (WFP Status=Potential): Month $ = Contract × month%.
//    Win-confidence is shown but NOT applied (Patrick's rule).
//  · Anything not spread lands in a "Later" bucket (the sheet's "2029 & after").
//  · Monthly Revenue = active subtotal + probable subtotal, charted with the
//    awarded/pipeline split, declining backlog, and revenue per staff dollar.
//
// Editing is finance/admin only; everyone else sees it read-only.

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import Icon from "../components/Icon";
import { useData } from "../data/DataProvider";
import GCBurnDown, { CompanyBurnDown, fmtMoney } from "../components/GCBurnDown";
import { roleFamily, positionBucket, POSITION_ORDER, isOpenSeat } from "../services/roles";
import { monthlyRateForRole } from "../services/rates";
import { isBenchEligible } from "../services/utilization";
import { fetchProcoreBudget } from "../services/procore";
import "../styles.css";

// ── month helpers ────────────────────────────────────────────────────────────
const keyOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const currentMonthKey = () => keyOf(new Date());
const addKey = (key, n) => {
  const [y, m] = key.split("-").map(Number);
  const d = new Date(y, m - 1 + n, 1);
  return keyOf(d);
};
const keyLabel = (key) => {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "short", year: "2-digit" }).replace(" ", " '");
};
const keysBetween = (a, b) => {
  const out = [];
  let k = a;
  while (k <= b && out.length < 72) { out.push(k); k = addKey(k, 1); }
  return out;
};
const monthBounds = (key) => {
  const [y, m] = key.split("-").map(Number);
  return [new Date(y, m - 1, 1), new Date(y, m, 0, 23, 59, 59)];
};

const round2 = (n) => Math.round(n * 100) / 100;
const pctFmt = (n) => (n == null || n === 0 ? "" : String(round2(n)));
// Matrix money: one fixed shape per magnitude ($18.4M / $492k / $950) so a column
// scans cleanly. Zero renders blank — a row of "$0" is noise, not information.
const cellMoney = (n) => {
  if (!n || isNaN(n)) return "";
  const a = Math.abs(n);
  if (a >= 999500) return `$${(n / 1000000).toFixed(1)}M`;
  if (a >= 1000) return `$${Math.round(n / 1000)}k`;
  return `$${Math.round(n)}`;
};

// ── page ─────────────────────────────────────────────────────────────────────
export default function Financials() {
  const { projects, people, teamMembers, billableRates, loading, error, isFinance,
    getRunoffEntries, addRunoffEntry, editRunoffEntry, removeRunoffEntry, editProject, getToken } = useData();

  const [view, setView] = useState("runoff");
  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(true);
  const [entriesError, setEntriesError] = useState(null);
  const [showPast, setShowPast] = useState(false);
  const [unlockPast, setUnlockPast] = useState(false); // finance can edit already-finished months
  const [showDollars, setShowDollars] = useState(true);
  const [extraMonths, setExtraMonths] = useState(0);
  const [selMonth, setSelMonth] = useState(currentMonthKey());
  const [saveMsg, setSaveMsg] = useState("");
  const [jobEdit, setJobEdit] = useState(null); // { id, ContractAmount, PercentComplete }
  const [pullingContract, setPullingContract] = useState(false);

  // Plain function (provider functions aren't memoized); mount-only effect.
  // quiet=true skips the loading flag so an in-place cell save doesn't unmount
  // the matrix table (which was jumping the page to the top on every save).
  const loadEntries = async (quiet = false) => {
    if (!quiet) setEntriesLoading(true);
    setEntriesError(null);
    try { setEntries(await getRunoffEntries()); }
    catch (e) { setEntriesError(e.message || "Couldn't load runoff data."); }
    finally { if (!quiet) setEntriesLoading(false); }
  };
  useEffect(() => { loadEntries(); }, []);

  const listMissing = /could not find the "runoffpercents" list/i.test(entriesError || "");

  // pct[job][month] = { id, value }
  const pct = useMemo(() => {
    const m = {};
    for (const e of entries) {
      if (!e.ProjectJobNumber || !e.Month) continue;
      (m[e.ProjectJobNumber] = m[e.ProjectJobNumber] || {})[e.Month] = { id: e.Id, value: Number(e.Percent) || 0 };
    }
    return m;
  }, [entries]);

  const activeJobs = useMemo(() => (projects || [])
    .filter(p => (p.Status || "Active") === "Active")
    .sort((a, b) => (a.Title || "").localeCompare(b.Title || "")), [projects]);
  const probableJobs = useMemo(() => (projects || [])
    .filter(p => p.Status === "Potential")
    .sort((a, b) => (a.JobName || a.Title || "").localeCompare(b.JobName || b.Title || "")), [projects]);
  // Completed jobs are NOT auto-listed — only ones finance has pulled in (persisted flag).
  const completedJobs = useMemo(() => (projects || [])
    .filter(p => p.Status === "Completed" && p.ShowInRunoff === true)
    .sort((a, b) => (a.JobName || a.Title || "").localeCompare(b.JobName || b.Title || "")), [projects]);
  // Completed jobs not yet in the matrix — the "+ Add completed job" picker list.
  const completedPickable = useMemo(() => (projects || [])
    .filter(p => p.Status === "Completed" && p.ShowInRunoff !== true)
    .sort((a, b) => (a.Title || "").localeCompare(b.Title || "")), [projects]);

  // ── visible month columns ──
  const nowKey = currentMonthKey();
  const months = useMemo(() => {
    let maxK = addKey(nowKey, 11);
    for (const job of Object.keys(pct)) {
      for (const k of Object.keys(pct[job])) {
        if (pct[job][k].value > 0 && k > maxK) maxK = k;
      }
    }
    // Default view = last month + current month; "Show past months" widens the
    // left edge to the trailing 6 months (keyed history isn't used for the edge).
    const start = showPast ? addKey(nowKey, -6) : addKey(nowKey, -1);
    return keysBetween(start, addKey(maxK, extraMonths));
  }, [pct, showPast, extraMonths, nowKey]);

  // ── per-job math (mirrors the sheet) ──
  const jobCalc = useCallback((p, kind) => {
    const jobPct = pct[p.Title] || {};
    const sumPct = Object.values(jobPct).reduce((a, v) => a + v.value, 0);
    const contract = Number(p.ContractAmount) || 0;
    const complete = Number(p.PercentComplete) || 0;
    const backlog = kind === "active" ? contract * (1 - complete / 100) : contract;
    const dollarsAt = (k) => {
      const v = jobPct[k]?.value || 0;
      if (!v) return 0;
      if (kind === "active") return sumPct > 0 ? backlog * (v / sumPct) : 0;
      return contract * (v / 100);
    };
    const spread = Object.entries(jobPct).reduce((a, [k, v]) => a + (v.value ? dollarsAt(k) : 0), 0);
    const later = Math.max(0, backlog - spread);
    // Validation: keyed % should equal % remaining (active) or 100% (probable).
    const expected = kind === "active" ? Math.max(0, 100 - complete) : 100;
    const drift = round2(sumPct - expected);
    return { jobPct, sumPct, contract, complete, backlog, dollarsAt, later, drift };
  }, [pct]);

  const calc = useMemo(() => {
    const perJob = {};
    activeJobs.forEach(p => { perJob[p.Title] = jobCalc(p, "active"); });
    probableJobs.forEach(p => { perJob[p.Title] = jobCalc(p, "probable"); });
    // Completed jobs use the same backlog-basis math as Under-Contract jobs.
    completedJobs.forEach(p => { perJob[p.Title] = jobCalc(p, "active"); });
    const activeAt = (k) => activeJobs.reduce((a, p) => a + perJob[p.Title].dollarsAt(k), 0);
    const probableAt = (k) => probableJobs.reduce((a, p) => a + perJob[p.Title].dollarsAt(k), 0);
    const completedAt = (k) => completedJobs.reduce((a, p) => a + perJob[p.Title].dollarsAt(k), 0);
    return { perJob, activeAt, probableAt, completedAt };
  }, [activeJobs, probableJobs, completedJobs, jobCalc]);

  // ── staff cost (excl. PX & accountants — Patrick's rule) + composition ──
  const staff = useMemo(() => {
    const counted = [], unrated = [];
    (people || []).filter(p => p.IsActive !== false).forEach(p => {
      const fam = roleFamily(p.CompanyRole);
      if (fam === "px" || fam === "accountant") return;
      const rate = monthlyRateForRole(p.CompanyRole, billableRates);
      if (rate > 0) counted.push({ person: p, rate }); else unrated.push(p);
    });
    const byBucket = {};
    counted.forEach(({ person, rate }) => {
      const b = positionBucket(person.CompanyRole);
      const s = byBucket[b] = byBucket[b] || { count: 0, total: 0 };
      s.count++; s.total += rate;
    });
    const total = counted.reduce((a, c) => a + c.rate, 0);
    return { counted, unrated, byBucket, total };
  }, [people, billableRates]);

  // ── float: ops staff with no active-job assignment covering a month ──
  // Split into pure float vs. float already tagged to a Potential (pipeline) job
  // for the month — the latter has a prospective home but still produces $0 until
  // the job is won. Returns [{ person, pipelineJobs: [jobName…] }].
  const floatDetailFor = useCallback((k) => {
    const [ms, me] = monthBounds(k);
    const activeTitles = new Set(activeJobs.map(p => p.Title));
    const potentialName = {};
    probableJobs.forEach(p => { potentialName[p.Title] = p.JobName || p.Title; });
    const covered = new Set();
    const pipelineBy = {}; // personName -> Set(pipeline job names covering the month)
    (teamMembers || []).forEach(m => {
      if (isOpenSeat(m)) return;
      const inActive = activeTitles.has(m.ProjectJobNumber);
      const inPotential = Object.prototype.hasOwnProperty.call(potentialName, m.ProjectJobNumber);
      if (!inActive && !inPotential) return;
      const s = m.StartDate ? new Date(m.StartDate) : null;
      const e = m.EndDate ? new Date(m.EndDate) : null;
      if (!((!s || s <= me) && (!e || e >= ms))) return;
      if (inActive) covered.add(m.PersonName);
      if (inPotential) (pipelineBy[m.PersonName] = pipelineBy[m.PersonName] || new Set()).add(potentialName[m.ProjectJobNumber]);
    });
    return (people || [])
      .filter(p => p.IsActive !== false && isBenchEligible(p) && !covered.has(p.FullName))
      .sort((a, b) => a.FullName.localeCompare(b.FullName))
      .map(p => ({ person: p, pipelineJobs: [...(pipelineBy[p.FullName] || [])] }));
  }, [teamMembers, people, activeJobs, probableJobs]);

  const floatNow = useMemo(() => floatDetailFor(selMonth), [floatDetailFor, selMonth]);
  const pipeFloatNow = useMemo(() => floatNow.filter(f => f.pipelineJobs.length > 0), [floatNow]);
  const idleFloatNow = useMemo(() => floatNow.filter(f => f.pipelineJobs.length === 0), [floatNow]);
  // Ops headcount for the utilization bars: on active jobs vs floating (split).
  const opsCount = useMemo(() => (people || []).filter(p => p.IsActive !== false && isBenchEligible(p)).length, [people]);
  const staffingFor = useCallback((k) => {
    const detail = floatDetailFor(k);
    const pipeline = detail.filter(f => f.pipelineJobs.length > 0).length;
    return { assigned: Math.max(0, opsCount - detail.length), pipeline, pure: detail.length - pipeline, float: detail.length };
  }, [floatDetailFor, opsCount]);
  const revenueSel = calc.activeAt(selMonth) + calc.probableAt(selMonth) + calc.completedAt(selMonth);
  const perDollar = staff.total > 0 ? revenueSel / staff.total : 0;
  const totalBacklogNow = activeJobs.reduce((a, p) => a + calc.perJob[p.Title].backlog, 0);

  // ── cell editing ──
  const saveCell = async (job, month, raw) => {
    const value = raw === "" ? 0 : Math.max(0, Number(raw) || 0);
    const existing = pct[job]?.[month];
    if ((existing?.value || 0) === value) return;
    setSaveMsg("Saving…");
    try {
      if (existing?.id) {
        if (value > 0) await editRunoffEntry(existing.id, { Percent: value });
        else await removeRunoffEntry(existing.id);
      } else if (value > 0) {
        // The API resolves ProjectJobNumber to a project by exact match and
        // MERGEs on (job, month), so send the plain job number.
        await addRunoffEntry({ ProjectJobNumber: job, Month: `${month}-01`, Percent: value });
      }
      await loadEntries(true); // quiet refresh — keep the table mounted (no scroll jump)
      setSaveMsg("✓ Saved");
      setTimeout(() => setSaveMsg(""), 1500);
    } catch (e) { setSaveMsg("✗ " + (e.message || "Save failed")); }
  };

  const saveJobFinancials = async () => {
    if (!jobEdit) return;
    setSaveMsg("Saving…");
    try {
      await editProject(jobEdit.id, {
        ContractAmount: jobEdit.ContractAmount === "" ? null : Number(jobEdit.ContractAmount),
        PercentComplete: jobEdit.PercentComplete === "" ? null : Number(jobEdit.PercentComplete),
      });
      setJobEdit(null); setSaveMsg("✓ Saved");
      setTimeout(() => setSaveMsg(""), 1500);
    } catch (e) { setSaveMsg("✗ " + (e.message || "Save failed")); }
  };

  // Pull the total Revised Budget from Procore (Hardaway View REV 1) into the open
  // contract editor. Finance reviews the pulled number, then saves it.
  const pullContract = async (p) => {
    if (!p.ProcoreProjectId) { setSaveMsg("✗ No Procore Project ID on this job"); setTimeout(() => setSaveMsg(""), 2500); return; }
    setPullingContract(true); setSaveMsg("Pulling Procore budget…");
    try {
      const data = await fetchProcoreBudget(p.ProcoreProjectId, await getToken());
      const total = Math.round(Number(data.revisedBudgetTotal) || 0);
      setJobEdit(prev => (prev && prev.id === p.Id) ? { ...prev, ContractAmount: total } : { id: p.Id, ContractAmount: total, PercentComplete: p.PercentComplete ?? "" });
      setSaveMsg(total ? `✓ Pulled ${fmtMoney(total)} — review, then save` : "✗ Procore returned $0");
      setTimeout(() => setSaveMsg(""), 3500);
    } catch (e) { setSaveMsg("✗ " + (e.message || "Procore pull failed")); }
    finally { setPullingContract(false); }
  };

  // ── completed jobs: pull one into / out of the runoff matrix ──
  // The include state is a persisted ShowInRunoff flag on the job.
  const addCompletedJob = async (id) => {
    if (!id) return;
    setSaveMsg("Adding…");
    try {
      await editProject(id, { ShowInRunoff: true });
      setSaveMsg("✓ Added");
      setTimeout(() => setSaveMsg(""), 1500);
    } catch (e) { setSaveMsg("✗ " + (e.message || "Couldn't add job")); }
  };
  const removeCompletedJob = async (p) => {
    setSaveMsg("Removing…");
    try {
      await editProject(p.Id, { ShowInRunoff: false });
      setSaveMsg("✓ Removed");
      setTimeout(() => setSaveMsg(""), 1500);
    } catch (e) { setSaveMsg("✗ " + (e.message || "Couldn't remove job")); }
  };

  if (loading) return <div className="page"><div className="empty"><div className="spinner" /><div className="empty-text" style={{ marginTop: 16 }}>Loading financials…</div></div></div>;
  if (error) return <div className="page"><div className="empty"><div className="empty-text">Couldn't load data: {error}</div></div></div>;

  const chartMonths = months.slice(0, 30);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Financials</div>
          <div className="page-subtitle">Revenue runoff & GC burn-down · {isFinance ? "you can edit" : "read-only"}</div>
        </div>
        <div className="stat-row">
          <div className="stat-card"><div className="stat-bar" style={{ background: "var(--green-bar)" }} /><div className="stat-num">{fmtMoney(revenueSel)}</div><div className="stat-label">{keyLabel(selMonth)} Revenue</div></div>
          <div className="stat-card"><div className="stat-bar" style={{ background: "var(--navy-mid)" }} /><div className="stat-num">{fmtMoney(totalBacklogNow)}</div><div className="stat-label">Backlog (Contract)</div></div>
          <div className="stat-card"><div className="stat-bar" style={{ background: "var(--gold)" }} /><div className="stat-num">{fmtMoney(staff.total)}</div><div className="stat-label">Staff Cost / Mo</div></div>
          <div className="stat-card"><div className="stat-bar" style={{ background: perDollar >= 20 ? "var(--green-bar)" : "var(--red-bar)" }} /><div className="stat-num">${round2(perDollar)}</div><div className="stat-label">Rev per Staff $</div></div>
        </div>
      </div>

      <div className="wp-view-toggle">
        <button className={`wp-view-btn ${view === "runoff" ? "active" : ""}`} onClick={() => setView("runoff")}><Icon name="chart" size={15} /> Revenue Runoff</button>
        <button className={`wp-view-btn ${view === "gc" ? "active" : ""}`} onClick={() => setView("gc")}><Icon name="dollar" size={15} /> GC Burn-Down</button>
      </div>

      {view === "gc" ? <GCTab /> : (
        <>
          {listMissing ? (
            <div className="card mb-16" style={{ borderColor: "var(--gold)" }}>
              <div className="card-title" style={{ marginBottom: 8 }}>One-time setup needed</div>
              <div style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6 }}>
                The <b>RunoffPercents</b> SharePoint list doesn't exist yet. Create it on the CultureIndex site with columns:
                <b> ProjectJobNumber</b> (text), <b>Month</b> (date), <b>Percent</b> (number) — then reload. The Projects list also
                needs <b>ContractAmount</b> (number) and <b>PercentComplete</b> (number) columns.
              </div>
            </div>
          ) : entriesError ? (
            <div className="card mb-16" style={{ borderColor: "var(--red)", color: "var(--red)", fontSize: 13 }}>{entriesError}</div>
          ) : null}

          {/* Chart: staffing utilization bars + revenue lines (Pat's read) */}
          <div className="card mb-16">
            <div className="card-header">
              <div className="card-title">Staffing vs Revenue <span style={{ fontWeight: 600 }}>· click a month to inspect it</span></div>
              <div className="legend-row" style={{ margin: 0 }}>
                <span className="legend-item"><span className="legend-swatch" style={{ background: "#b7c3dd" }} /> On active jobs</span>
                <span className="legend-item"><span className="legend-swatch" style={{ background: "var(--gold-text)" }} /> Float · prospective home</span>
                <span className="legend-item"><span className="legend-swatch" style={{ background: "#e879a6" }} /> Float · no home</span>
                <span className="legend-item"><span style={{ width: 14, borderTop: "2.5px solid var(--navy-mid)", display: "inline-block" }} /> Revenue — awarded</span>
                <span className="legend-item"><span style={{ width: 14, borderTop: "2.5px dashed var(--gold-text)", display: "inline-block" }} /> Revenue — with pipeline</span>
              </div>
            </div>
            <StaffRevChart months={chartMonths} calc={calc} staffingFor={staffingFor} selMonth={selMonth} onSelect={setSelMonth} />
          </div>

          {/* Staff cost + float */}
          <div className="grid-2-resp mb-16" style={{ alignItems: "start" }}>
            <div className="card">
              <div className="card-header">
                <div className="card-title">Staff Cost Composition</div>
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{staff.counted.length} counted · PX & accountants excluded</div>
              </div>
              {POSITION_ORDER.filter(b => staff.byBucket[b]).map(b => (
                <div key={b} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid var(--border)", fontSize: 13 }}>
                  <span><b>{staff.byBucket[b].count}</b> {b}</span>
                  <span style={{ fontWeight: 700 }}>{fmtMoney(staff.byBucket[b].total)}/mo</span>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", padding: "9px 0 0", fontSize: 14, fontWeight: 800 }}>
                <span>Total monthly staff cost</span><span>{fmtMoney(staff.total)}</span>
              </div>
              {staff.unrated.length > 0 && (
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 8 }}>
                  Not counted (no billable rate): {staff.unrated.slice(0, 6).map(p => p.FullName).join(", ")}{staff.unrated.length > 6 ? ` +${staff.unrated.length - 6} more` : ""}
                </div>
              )}
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>
                Monthly-ized from the Rates tab (weekly × 52 ÷ 12). {keyLabel(selMonth)}: {fmtMoney(revenueSel)} ÷ {fmtMoney(staff.total)} = <b>${round2(perDollar)} of revenue per staff dollar</b>.
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <div className="card-title">Float — {keyLabel(selMonth)}</div>
                <div style={{ fontSize: 12, fontWeight: 800, color: floatNow.length ? "var(--red)" : "var(--green)" }}>{floatNow.length} producing $0{pipeFloatNow.length > 0 ? ` · ${pipeFloatNow.length} with a prospective home` : ""}</div>
              </div>
              {floatNow.length === 0 ? (
                <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Everyone in operations is on an active job this month.</div>
              ) : (
                <>
                  {idleFloatNow.length > 0 && (
                    <>
                      <div className="fin-float-head">No home · {idleFloatNow.length}</div>
                      {idleFloatNow.map(({ person: p }) => (
                        <Link key={p.Id} to={`/people/${p.Id}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "7px 0", borderBottom: "1px solid var(--border)", fontSize: 13, textDecoration: "none", color: "inherit" }}>
                          <span style={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{p.FullName}</span>
                          <span style={{ color: "var(--text-muted)", whiteSpace: "nowrap" }}>{p.CompanyRole} · {fmtMoney(monthlyRateForRole(p.CompanyRole, billableRates))}/mo idle</span>
                        </Link>
                      ))}
                    </>
                  )}
                  {pipeFloatNow.length > 0 && (
                    <>
                      <div className="fin-float-head" style={{ color: "var(--gold-text)" }}>Prospective home · {pipeFloatNow.length}</div>
                      {pipeFloatNow.map(({ person: p, pipelineJobs }) => (
                        <Link key={p.Id} to={`/people/${p.Id}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "7px 0", borderBottom: "1px solid var(--border)", fontSize: 13, textDecoration: "none", color: "inherit" }}>
                          <span style={{ fontWeight: 700, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.FullName}<span style={{ color: "var(--gold-text)", fontWeight: 700 }}> → {pipelineJobs.join(", ")}</span></span>
                          <span style={{ color: "var(--text-muted)", whiteSpace: "nowrap" }}>{p.CompanyRole} · {fmtMoney(monthlyRateForRole(p.CompanyRole, billableRates))}/mo</span>
                        </Link>
                      ))}
                    </>
                  )}
                </>
              )}
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 8 }}>Ops staff with no active-job assignment covering the month. A <b style={{ color: "var(--gold-text)" }}>prospective home</b> means they're pencilled onto a pipeline job — still $0 until it's won.</div>
            </div>
          </div>

          {/* Matrix controls */}
          <div className="section-header">
            <div className="section-title">Runoff Matrix <span style={{ fontWeight: 600, color: "var(--text-muted)" }}>· % of contract per month{isFinance ? " — type in a cell, saves on blur" : ""}</span></div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              {saveMsg && <span style={{ fontSize: 12, fontWeight: 700, color: saveMsg.startsWith("✗") ? "var(--red)" : "var(--green)" }}>{saveMsg}</span>}
              <button className="btn btn-ghost" style={{ fontSize: 12, padding: "5px 12px" }} onClick={() => setShowDollars(s => !s)}>{showDollars ? "Hide $" : "Show $"}</button>
              <button className="btn btn-ghost" style={{ fontSize: 12, padding: "5px 12px" }} onClick={() => setShowPast(s => !s)}>{showPast ? "Hide past months" : "Show past months"}</button>
              {isFinance && !listMissing && (
                <button className={`btn ${unlockPast ? "btn-secondary" : "btn-ghost"}`} style={{ fontSize: 12, padding: "5px 12px" }} title="Edit already-finished months when the actual billings come in — lock again when done" onClick={() => { if (unlockPast) setUnlockPast(false); else { setUnlockPast(true); setShowPast(true); } }}>
                  <Icon name={unlockPast ? "unlock" : "lock"} size={12} style={{ verticalAlign: "middle", marginRight: 4 }} /> {unlockPast ? "Lock past months" : "Unlock past months"}
                </button>
              )}
              <button className="btn btn-ghost" style={{ fontSize: 12, padding: "5px 12px" }} onClick={() => setExtraMonths(n => n + 3)}><Icon name="plus" size={12} style={{ verticalAlign: "middle", marginRight: 4 }} /> Add months</button>
              {isFinance && !listMissing && completedPickable.length > 0 && (
                <select className="form-select" style={{ fontSize: 12, padding: "5px 10px", maxWidth: 240 }} value="" onChange={e => addCompletedJob(e.target.value)} title="Pull a completed job into the matrix">
                  <option value="">+ Add completed job…</option>
                  {completedPickable.map(p => <option key={p.Id} value={p.Id}>{p.Title} — {p.JobName}</option>)}
                </select>
              )}
            </div>
          </div>
          {unlockPast && (
            <div style={{ fontSize: 12, color: "var(--gold-text)", fontWeight: 700, margin: "0 0 10px", display: "flex", alignItems: "center", gap: 6 }}>
              <Icon name="unlock" size={13} /> Past months are unlocked — the highlighted earlier cells are editable. Lock them again when you're done.
            </div>
          )}
          {entriesLoading ? (
            <div className="empty"><div className="spinner" /><div className="empty-text" style={{ marginTop: 16 }}>Loading matrix…</div></div>
          ) : (
            <div className="fin-wrap">
              <table className="fin-table">
                <thead>
                  <tr>
                    <th className="fin-sticky">Job</th>
                    <th className="fin-num">Contract</th>
                    <th className="fin-num">% Compl</th>
                    <th className="fin-num fin-divide">Backlog</th>
                    {months.map(k => (
                      <th key={k} className={`fin-num fin-month ${k === selMonth ? "sel" : ""} ${k < nowKey ? "past" : ""}`} onClick={() => setSelMonth(k)}>{keyLabel(k)}</th>
                    ))}
                    <th className="fin-num fin-divide">Later</th>
                    <th className="fin-num">Σ%</th>
                  </tr>
                </thead>
                <tbody>
                  <SectionRows label={`Jobs Under Contract · ${activeJobs.length}`} jobs={activeJobs} kind="active" months={months} calc={calc} selMonth={selMonth}
                    isFinance={isFinance && !listMissing} unlockPast={unlockPast} showDollars={showDollars} nowKey={nowKey} saveCell={saveCell} onPullContract={pullContract} pullingContract={pullingContract}
                    jobEdit={jobEdit} setJobEdit={setJobEdit} saveJobFinancials={saveJobFinancials} />
                  <SubtotalRow label="Sub Total — Under Contract" months={months} at={calc.activeAt} jobs={activeJobs} calc={calc} selMonth={selMonth} />
                  <SectionRows label={`Probable Future Jobs · ${probableJobs.length}`} jobs={probableJobs} kind="probable" months={months} calc={calc} selMonth={selMonth}
                    isFinance={isFinance && !listMissing} unlockPast={unlockPast} showDollars={showDollars} nowKey={nowKey} saveCell={saveCell} onPullContract={pullContract} pullingContract={pullingContract}
                    jobEdit={jobEdit} setJobEdit={setJobEdit} saveJobFinancials={saveJobFinancials} />
                  <SubtotalRow label="Sub Total — Probable" className="probable" months={months} at={calc.probableAt} jobs={probableJobs} calc={calc} selMonth={selMonth} />
                  {completedJobs.length > 0 && (
                    <>
                      <SectionRows label={`Completed Jobs · ${completedJobs.length}`} jobs={completedJobs} kind="active" months={months} calc={calc} selMonth={selMonth}
                        isFinance={isFinance && !listMissing} unlockPast={unlockPast} showDollars={showDollars} nowKey={nowKey} saveCell={saveCell} onPullContract={pullContract} pullingContract={pullingContract}
                        jobEdit={jobEdit} setJobEdit={setJobEdit} saveJobFinancials={saveJobFinancials} onRemove={removeCompletedJob} />
                      <SubtotalRow label="Sub Total — Completed" months={months} at={calc.completedAt} jobs={completedJobs} calc={calc} selMonth={selMonth} />
                    </>
                  )}
                  <tr className="fin-grand">
                    <td className="fin-sticky">Projected Monthly Revenue</td>
                    <td /><td /><td className="fin-num fin-divide">{cellMoney(totalBacklogNow)}</td>
                    {months.map(k => <td key={k} className={`fin-num ${k === selMonth ? "sel" : ""}`}>{cellMoney(calc.activeAt(k) + calc.probableAt(k) + calc.completedAt(k))}</td>)}
                    <td className="fin-divide" /><td />
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 10, lineHeight: 1.6 }}>
            Jobs come straight from the Planner — Active = "Under Contract", Potential = "Probable". Add or reclassify jobs there.
            Monthly workflow (mirrors Patrick's): when the WIP is final, update each job's <b>% Complete</b>, and the just-finished month
            rolls into history automatically — nothing gets deleted. The <b>Σ%</b> chip flags any job whose keyed months don't add up to
            its remaining percent.
          </div>
        </>
      )}
    </div>
  );
}

// ── matrix section rows ──────────────────────────────────────────────────────
function SectionRows({ label, jobs, kind, months, calc, selMonth, isFinance, unlockPast, showDollars, nowKey, saveCell, jobEdit, setJobEdit, saveJobFinancials, onRemove, onPullContract, pullingContract }) {
  return (
    <>
      <tr className={`fin-section ${kind === "probable" ? "probable" : ""}`}><td className="fin-sticky" colSpan={4}>{label}</td><td colSpan={months.length + 2} /></tr>
      {jobs.length === 0 && (
        <tr><td className="fin-sticky" colSpan={4} style={{ color: "var(--text-muted)", fontSize: 12 }}>None in the Planner yet.</td><td colSpan={months.length + 2} /></tr>
      )}
      {jobs.map(p => {
        const c = calc.perJob[p.Title];
        const editing = jobEdit && jobEdit.id === p.Id;
        const missingSetup = !c.contract;
        return (
          <tr key={p.Title} className={`fin-row ${kind === "probable" ? "probable" : ""}`}>
            <td className="fin-sticky">
              <div className="fin-job">{p.JobName || p.Title}{kind === "probable" && p.Confidence != null && <span className="fin-conf">{p.Confidence}%</span>}</div>
              <div className="fin-job-meta"><span className="fin-job-num">{p.Title}</span>{missingSetup && <span className="fin-needs">Needs contract</span>}</div>
              {onRemove && isFinance && (
                <button className="fin-linklike" style={{ fontSize: 11, color: "var(--red)" }} title="Remove this job from the matrix" onClick={() => onRemove(p)}>remove from matrix</button>
              )}
            </td>
            {editing ? (
              <td colSpan={2}>
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  <input className="form-input fin-inline" type="number" placeholder="Contract value $" title="Contract value ($)" autoFocus value={jobEdit.ContractAmount}
                    onChange={e => setJobEdit({ ...jobEdit, ContractAmount: e.target.value })} />
                  {kind === "active" && <input className="form-input fin-inline" type="number" placeholder="% done" title="% complete" style={{ maxWidth: 76 }} value={jobEdit.PercentComplete}
                    onChange={e => setJobEdit({ ...jobEdit, PercentComplete: e.target.value })} />}
                  {onPullContract && p.ProcoreProjectId && (
                    <button className="btn btn-ghost" style={{ fontSize: 11, padding: "3px 8px", whiteSpace: "nowrap" }} title="Pull the total Revised Budget from Procore (Hardaway View REV 1)" onClick={() => onPullContract(p)} disabled={pullingContract}>
                      <Icon name="download" size={11} style={{ verticalAlign: "middle", marginRight: 3 }} />Procore
                    </button>
                  )}
                  <button className="btn btn-primary" style={{ fontSize: 11, padding: "3px 8px" }} onClick={saveJobFinancials}>✓</button>
                  <button className="btn btn-ghost" style={{ fontSize: 11, padding: "3px 8px" }} onClick={() => setJobEdit(null)}>✕</button>
                </div>
              </td>
            ) : (
              <>
                <td className="fin-num">
                  {isFinance ? (
                    <button className={`fin-linklike ${c.contract ? "" : "fin-set"}`} title={c.contract ? "Edit contract value / % complete" : "Enter this job's contract value (and % complete) so its runoff dollars can be calculated"} onClick={() => setJobEdit({ id: p.Id, ContractAmount: c.contract || "", PercentComplete: p.PercentComplete ?? "" })}>
                      {c.contract ? cellMoney(c.contract) : "+ Add contract"}
                    </button>
                  ) : (c.contract ? cellMoney(c.contract) : <span className="fin-dash">—</span>)}
                </td>
                <td className="fin-num fin-quiet">{kind === "active" ? (p.PercentComplete != null ? `${round2(p.PercentComplete)}%` : <span className="fin-dash">—</span>) : ""}</td>
              </>
            )}
            <td className="fin-num fin-divide fin-strong">{c.contract ? cellMoney(c.backlog) : <span className="fin-dash">—</span>}</td>
            {months.map(k => {
              const cellVal = c.jobPct[k]?.value || 0;
              const dollars = c.dollarsAt(k);
              const past = k < nowKey;
              const editable = isFinance && (!past || unlockPast);
              return (
                <td key={k} className={`fin-cell ${past ? (editable ? "unlocked" : "past") : ""} ${k === selMonth ? "sel" : ""}`}>
                  {editable ? (
                    <span className="fin-cellwrap">
                      {/* placeholder=" " lets CSS hide the % sign while the cell is empty */}
                      <input className="fin-pct" type="number" min="0" step="0.5" placeholder=" " defaultValue={pctFmt(cellVal)} key={`${p.Title}-${k}-${cellVal}`}
                        onBlur={e => saveCell(p.Title, k, e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }} />
                      <span className="fin-suffix">%</span>
                    </span>
                  ) : cellVal ? (
                    <span className="fin-cellwrap">
                      <span className="fin-pct-ro">{pctFmt(cellVal)}</span>
                      <span className="fin-suffix">%</span>
                    </span>
                  ) : null}
                  {showDollars && dollars > 0 && <div className="fin-dollars">{cellMoney(dollars)}</div>}
                </td>
              );
            })}
            <td className="fin-num fin-divide fin-quiet">{c.later > 0.5 && c.contract ? cellMoney(c.later) : ""}</td>
            <td className="fin-num">
              {c.contract > 0 && (
                Math.abs(c.drift) <= 0.5
                  ? <span className="fin-check ok" title="Keyed % matches remaining">✓ {round2(c.sumPct)}%</span>
                  : <span className="fin-check off" title={`Keyed ${round2(c.sumPct)}% vs ${kind === "active" ? `${round2(100 - c.complete)}% remaining` : "100%"}`}>{round2(c.sumPct)}%</span>
              )}
            </td>
          </tr>
        );
      })}
    </>
  );
}

function SubtotalRow({ label, className = "", months, at, jobs, calc, selMonth }) {
  const backlog = jobs.reduce((a, p) => a + (calc.perJob[p.Title]?.backlog || 0), 0);
  return (
    <tr className={`fin-subtotal ${className}`}>
      <td className="fin-sticky">{label}</td>
      <td /><td /><td className="fin-num fin-divide">{cellMoney(backlog)}</td>
      {months.map(k => <td key={k} className={`fin-num ${k === selMonth ? "sel" : ""}`}>{cellMoney(at(k))}</td>)}
      <td className="fin-divide" /><td />
    </tr>
  );
}

// Monotone cubic (Fritsch–Carlson) path through the points: the curve passes
// exactly through every month's value and never overshoots between them, so
// smoothing the line can't invent a peak or dip that isn't in the data.
function smoothPath(pts) {
  const n = pts.length;
  if (n === 0) return "";
  if (n < 3) return pts.map(([px, py], i) => `${i ? "L" : "M"}${px.toFixed(1)},${py.toFixed(1)}`).join(" ");
  const dx = [], m = [];
  for (let i = 0; i < n - 1; i++) { dx[i] = pts[i + 1][0] - pts[i][0]; m[i] = (pts[i + 1][1] - pts[i][1]) / dx[i]; }
  const t = [m[0]];
  for (let i = 1; i < n - 1; i++) t[i] = m[i - 1] * m[i] <= 0 ? 0 : (m[i - 1] + m[i]) / 2;
  t[n - 1] = m[n - 2];
  for (let i = 0; i < n - 1; i++) {
    if (m[i] === 0) { t[i] = 0; t[i + 1] = 0; continue; }
    const a = t[i] / m[i], b = t[i + 1] / m[i], h = a * a + b * b;
    if (h > 9) { const k = 3 / Math.sqrt(h); t[i] = k * a * m[i]; t[i + 1] = k * b * m[i]; }
  }
  let d = `M${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)}`;
  for (let i = 0; i < n - 1; i++) {
    const [x0, y0] = pts[i], [x1, y1] = pts[i + 1], h = dx[i] / 3;
    d += ` C${(x0 + h).toFixed(1)},${(y0 + t[i] * h).toFixed(1)} ${(x1 - h).toFixed(1)},${(y1 - t[i + 1] * h).toFixed(1)} ${x1.toFixed(1)},${y1.toFixed(1)}`;
  }
  return d;
}

// ── chart: staffing utilization bars + revenue lines ─────────────────────────
// Pat's read: bars show the people — who's on active jobs vs floating (right
// axis, headcount). Lines show the money — awarded revenue solid, awarded +
// pipeline dashed (left axis, $). Click a month to inspect it below.
function StaffRevChart({ months, calc, staffingFor, selMonth, onSelect }) {
  const W = 940, H = 300, padL = 56, padR = 64, padT = 18, padB = 34;
  const innerW = W - padL - padR, innerH = H - padT - padB;
  const data = months.map(k => {
    const s = staffingFor(k);
    return { k, a: calc.activeAt(k), p: calc.probableAt(k), staffed: s.assigned, pipeline: s.pipeline, pure: s.pure, float: s.float };
  });
  const maxRev = Math.max(1, ...data.map(d => d.a + d.p));
  const maxHead = Math.max(1, ...data.map(d => d.staffed + d.float));
  const bw = innerW / Math.max(1, data.length);
  const x = (i) => padL + i * bw;
  const yR = (v) => padT + (1 - v / maxRev) * innerH;
  const yH = (v) => padT + (1 - v / maxHead) * innerH;
  const selIdx = data.findIndex(d => d.k === selMonth);
  const step = Math.max(1, Math.ceil(data.length / 12));
  const showLabel = (i) => (i === selIdx) || (i % step === 0 && (selIdx < 0 || Math.abs(i - selIdx) >= step));
  const anyPipeline = data.some(d => d.p > 0);
  const smooth = (fn) => smoothPath(data.map((d, i) => [x(i) + bw / 2, fn(d)]));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto" }}>
      {[0.25, 0.5, 0.75, 1].map(f => (
        <g key={f}>
          <line x1={padL} y1={yR(maxRev * f)} x2={W - padR} y2={yR(maxRev * f)} stroke="var(--border)" strokeWidth="1" opacity="0.6" />
          <text x={padL - 6} y={yR(maxRev * f) + 3} textAnchor="end" fontSize="9" fill="var(--text-muted)">{fmtMoney(maxRev * f)}</text>
        </g>
      ))}
      {/* right axis: headcount */}
      {[0.5, 1].map(f => (
        <text key={f} x={W - padR + 6} y={yH(maxHead * f) + 3} fontSize="9" fill="var(--text-muted)">{Math.round(maxHead * f)}</text>
      ))}
      <text x={W - padR + 6} y={padT - 6} fontSize="8" fill="var(--text-muted)">people</text>
      {/* staffing bars */}
      {data.map((d, i) => {
        const sel = d.k === selMonth;
        return (
          <g key={d.k} onClick={() => onSelect(d.k)} style={{ cursor: "pointer" }}>
            <rect x={x(i) + 1} y={padT} width={Math.max(1, bw - 2)} height={innerH} fill={sel ? "var(--blue-soft)" : "transparent"} />
            <rect x={x(i) + bw * 0.18} y={yH(d.staffed)} width={bw * 0.64} height={innerH - (yH(d.staffed) - padT)} fill="#b7c3dd" rx="1.5">
              <title>{`${keyLabel(d.k)} — on active jobs: ${d.staffed}`}</title>
            </rect>
            {d.pipeline > 0 && (
              <rect x={x(i) + bw * 0.18} y={yH(d.staffed + d.pipeline)} width={bw * 0.64} height={yH(d.staffed) - yH(d.staffed + d.pipeline)} fill="var(--gold-text)" rx="1.5">
                <title>{`${keyLabel(d.k)} — float with a prospective home: ${d.pipeline}`}</title>
              </rect>
            )}
            {d.pure > 0 && (
              <rect x={x(i) + bw * 0.18} y={yH(d.staffed + d.float)} width={bw * 0.64} height={yH(d.staffed + d.pipeline) - yH(d.staffed + d.float)} fill="#e879a6" rx="1.5">
                <title>{`${keyLabel(d.k)} — float, no home (producing $0): ${d.pure}`}</title>
              </rect>
            )}
            {showLabel(i) && <text x={x(i) + bw / 2} y={H - padB + 13} textAnchor="middle" fontSize="9" fontWeight={sel ? 800 : 500} fill={sel ? "var(--navy)" : "var(--text-muted)"}>{keyLabel(d.k)}</text>}
            <line x1={x(i) + bw / 2} y1={H - padB} x2={x(i) + bw / 2} y2={H - padB + 3} stroke="var(--border-mid)" strokeWidth="1" />
          </g>
        );
      })}
      {/* revenue lines on top */}
      {anyPipeline && <path d={smooth(d => yR(d.a + d.p))} fill="none" stroke="var(--gold-text)" strokeWidth="2.2" strokeDasharray="6 4" strokeLinecap="round" opacity="0.9" />}
      <path d={smooth(d => yR(d.a))} fill="none" stroke="var(--navy-mid)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
      {data.map((d, i) => (
        <g key={"dot" + d.k} onClick={() => onSelect(d.k)} style={{ cursor: "pointer" }}>
          {anyPipeline && <circle cx={x(i) + bw / 2} cy={yR(d.a + d.p)} r={d.k === selMonth ? 4 : 2.6} fill="#fff" stroke="var(--gold-text)" strokeWidth="1.6">
            <title>{`${keyLabel(d.k)} revenue w/ pipeline: ${fmtMoney(d.a + d.p)} (awarded ${fmtMoney(d.a)} + pipeline ${fmtMoney(d.p)})`}</title>
          </circle>}
          <circle cx={x(i) + bw / 2} cy={yR(d.a)} r={d.k === selMonth ? 4.2 : 2.8} fill="var(--navy-mid)" stroke="#fff" strokeWidth="1.2">
            <title>{`${keyLabel(d.k)} awarded revenue: ${fmtMoney(d.a)}`}</title>
          </circle>
        </g>
      ))}
    </svg>
  );
}

// ── GC burn-down sub-tab (moved from the Planner) ────────────────────────────
function GCTab() {
  const { projects, teamMembers, billableRates } = useData();
  const [gcProjectId, setGcProjectId] = useState("__company__");
  const activeProjects = (projects || []).filter(p => (p.Status || "Active") === "Active");
  return (
    <div>
      <div className="section-header">
        <div className="section-title">GC Burn-Down by Project</div>
        <select className="form-select" style={{ maxWidth: 340 }} value={gcProjectId} onChange={e => setGcProjectId(e.target.value)}>
          <option value="__company__">🏢 Company-wide (all active projects)</option>
          {[...(projects || [])].sort((a, b) => (a.Title || "").localeCompare(b.Title || "")).map(p => <option key={p.Id} value={p.Title}>{p.Title} — {p.JobName}</option>)}
        </select>
      </div>
      {gcProjectId === "__company__" ? (
        <CompanyBurnDown projects={activeProjects} teamMembers={(teamMembers || []).filter(m => !isOpenSeat(m))} rates={billableRates} />
      ) : (() => {
        const proj = (projects || []).find(p => p.Title === gcProjectId);
        if (!proj) return <div className="empty"><div className="empty-text">Pick a project to see its GC burn-down.</div></div>;
        return <GCBurnDown project={proj} members={(teamMembers || []).filter(m => m.ProjectJobNumber === gcProjectId && !isOpenSeat(m))} rates={billableRates} />;
      })()}
    </div>
  );
}
